import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../components/custom-table/custom-table";
import { sampleSections } from "../../../components/filter/sampleData";
import NewAssignModal from "../../../components/modals/newAssignModal/newAssignModal";
import NycbsStatusModal from "../../../components/modals/nycbsStatusModal/nycbsStatusModal";
import { PaginationKeys } from "../../../enums/nycbsPharmaOrder";
import { NycbsStatus } from "../../../enums/nycbsStatus";
import {
  appendNycbsPharmaOrdersFromApi,
  fetchNycbsPharmaOrdersFromApi,
} from "../../../redux/slice/nycbsPharmaExternal/nycbsPharmaOrder";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { logDataToConsole } from "../../../utils/customLogger";
import { NycbsPharmaTableColumn } from "../../../utils/nycbsPharmaTableColumns";
import { generateTableDataForNycbsPharma } from "../../../utils/nycbsPharmaTableData";

const NycbsPharmaTable = () => {
  const { data, loading, pageData } = useSelector(
    (state: RootState) => state.nycbsPharmaOrders,
  );
  const { data: providersDetails } = useSelector(
    (state: RootState) => state.allProviders,
  );
  const [pharmaPaOrders, setPharmaPaOrders] = useState<any[]>([]);
  const [batchNumberRetreived, setBatchNumberRetreived] = useState<number[]>(
    [],
  );
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [orderData, setOrderData] = useState<{
    orderId: string;
    assignedTo: string;
    assignedToId: string;
  }>();
  const [statusData, setStatusData] = useState<{
    orderId: string;
    status: string;
  }>();
  const dispatch = useDispatch<AppDispatch>();

  const initialLimit = 40;

  const handleEndIndexOfTable = (endIndex: number) => {
    if (
      data &&
      pageData?.[PaginationKeys.TotalCount] !== null &&
      endIndex < (pageData?.[PaginationKeys.TotalCount] ?? 0) - 1 &&
      endIndex === data.length - 1
    ) {
      const nextBatchFetch = Math.round(data.length / initialLimit);
      if (nextBatchFetch > 0) {
        setBatchNumberRetreived((prevBatchNumberRetrieved) => {
          if (!prevBatchNumberRetrieved.includes(nextBatchFetch)) {
            return [...prevBatchNumberRetrieved, nextBatchFetch];
          }
          return prevBatchNumberRetrieved;
        });
      }
    }
  };

  const handleAssignForOrders = (data: {
    id: string;
    assignee: string;
    assigneeId: string;
  }) => {
    const newData = {
      orderId: data.id,
      assignedTo: data.assignee,
      assignedToId: data.assigneeId,
    };
    setOrderData(newData);
    setShowModal(true);
  };

  const handleStatusForOrders = (data: { id: string; status: string }) => {
    const newData = {
      orderId: data.id,
      status: data.status,
    };
    setStatusData(newData);
    Number(data.status) !== NycbsStatus.UNASSIGNED && setShowStatusModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setOrderData(undefined);
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusData(undefined);
  };

  useEffect(() => {
    if (data) {
      logDataToConsole(
        "NycbsLayout",
        generateTableDataForNycbsPharma(data, providersDetails),
      );
      setPharmaPaOrders(
        generateTableDataForNycbsPharma(data, providersDetails),
      );
    }
  }, [data]);

  useEffect(() => {
    dispatch(appendNycbsPharmaOrdersFromApi(initialLimit));
  }, [JSON.stringify(batchNumberRetreived)]);

  useEffect(() => {
    if (showModal) {
      openModal("nycbs-reAssign");
    }
  }, [showModal]);

  useEffect(() => {
    showStatusModal && openModal("nycbs-status-modal");
  }, [showStatusModal]);
  return (
    <>
      <CustomTable
        tableHeaders={NycbsPharmaTableColumn}
        tableData={pharmaPaOrders}
        tableName={"nycbs-pharma-orders"}
        isFetching={loading}
        count={pageData?.[PaginationKeys.TotalCount]}
        pagesPerView={5}
        itemsPerPage={10}
        hideSearchBar={false}
        endIndexOfTable={handleEndIndexOfTable}
        onAssignToClick={(data) => handleAssignForOrders(data)}
        onStatusClick={(data) => handleStatusForOrders(data)}
        onRefreshButton={() =>
          dispatch(fetchNycbsPharmaOrdersFromApi(initialLimit))
        }
        filterSectionsData={sampleSections}
        showFilterButton={true}
        onFilterApplyClicked={() => logDataToConsole("Filter applied")}
      />
      {showModal && (
        <NewAssignModal
          onClose={handleModalClose}
          orderId={orderData?.orderId ?? ""}
          assignedTo={orderData?.assignedTo ?? ""}
          assignedToId={orderData?.assignedToId ?? ""}
          key={orderData?.orderId}
        />
      )}
      {showStatusModal && (
        <NycbsStatusModal
          onClose={handleStatusModalClose}
          orderId={statusData?.orderId ?? ""}
          status={
            Number(statusData?.status) in NycbsStatus
              ? Number(statusData?.status)
              : NycbsStatus.MARK_AS_COMPLETED
          }
        />
      )}
    </>
  );
};

export default NycbsPharmaTable;
